import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Loader2, Building2, User, MoreHorizontal, Phone, Trash2, CheckCircle2,
} from "lucide-react";
import { Badge, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@wa/ui";
import {
  type Lead, type LeadStatus,
  updateLead, deleteLead, convertLead,
} from "../../../lib/clients";
import { ConfirmModal } from "../_shared/ConfirmModal";
import { NextStepBadge } from "../_shared/NextStep";
import { useCallContext } from "../../telephony/CallProvider";
import { LeadSource } from "./LeadSource";
import { STATUSES, type T } from "./shared";

/** One lead on the board: the name, what we know about it, and its status. */
export function LeadCard({ lead, t, onUpdate, onDelete, onConvert, onPlanStep }: {
  lead: Lead;
  t: T;
  onUpdate: (id: string, status: LeadStatus) => void;
  onDelete: (id: string) => void;
  onConvert: (id: string) => void;
  /** Opens the planning dialog for this lead. Without it the marker is still
   *  drawn but is not a button - a card rendered somewhere with no dialog to
   *  open should not pretend it has one. */
  onPlanStep?: () => void;
}) {
  const nav = useNavigate();
  const call = useCallContext();
  const canCall = Boolean(call?.enabled) && Boolean(lead.phone);
  const [busy, setBusy] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  async function changeStatus(s: LeadStatus) {
    if (s === lead.status) return;
    setBusy(true);
    try { await updateLead(lead.id, { status: s }); onUpdate(lead.id, s); }
    finally { setBusy(false); }
  }

  async function doConvert() {
    setBusy(true);
    try { await convertLead(lead.id); onConvert(lead.id); }
    finally { setBusy(false); }
  }

  async function doDelete() {
    await deleteLead(lead.id);
    onDelete(lead.id);
  }

  return (
    <div className="relative rounded-lg border border-border bg-background p-3 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <button
          className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
          onClick={() => nav(`/leads/${lead.id}`)}
        >
          {lead.kind === "company"
            ? <Building2 className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
            : <User className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
          }
          <span className="truncate text-sm font-medium">{lead.name}</span>
        </button>
        <div className="relative">
          <button
            onClick={() => setShowMenu((v) => !v)}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <MoreHorizontal className="size-3.5" />
          </button>
          {showMenu && (
            <div
              className="absolute right-0 top-7 z-10 min-w-[150px] rounded-lg border border-border bg-background shadow-md"
              onMouseLeave={() => setShowMenu(false)}
            >
              {/* First, because it is the thing this board is for: a lead is a
                  company somebody still has to ring. The number itself stays off
                  the card - the card is a name and a stage - but the action does
                  not have to be behind the detail page. */}
              {canCall && (
                <button
                  onClick={() => {
                    setShowMenu(false);
                    call?.startCall({
                      phone: lead.phone!,
                      companyName: lead.name,
                      regCode: lead.regCode ?? undefined,
                      leadId: lead.id,
                      source: "manual",
                    });
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-accent"
                >
                  <Phone className="size-3.5" /> {t("common.call")}
                </button>
              )}
              {!lead.convertedClientId && (
                <button
                  onClick={() => { setShowMenu(false); doConvert(); }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-accent"
                >
                  <ArrowRight className="size-3.5" /> {t("leads.convert")}
                </button>
              )}
              <button
                onClick={() => { setShowMenu(false); setConfirmDel(true); }}
                className="flex w-full items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
              >
                <Trash2 className="size-3.5" /> {t("leads.delete")}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="mt-1.5 space-y-1">
        {lead.email && <p className="truncate text-xs text-muted-foreground">{lead.email}</p>}
        {lead.regCode && <p className="text-xs text-muted-foreground">{t("leads.regCode")}: {lead.regCode}</p>}
        {lead.valueCents != null && (
          <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
            {(lead.valueCents / 100).toLocaleString("et-EE", { style: "currency", currency: "EUR" })}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-1">
          {/* Where this record came from, and - for the two sources that have a
              page behind them - the way back to it. */}
          <LeadSource lead={lead} t={t} />
          {lead.convertedClientId && (
            <Badge variant="outline" className="gap-1 text-[10px] text-green-600">
              <CheckCircle2 className="size-2.5" /> {t("leads.converted")}
            </Badge>
          )}
          {/* Nothing at all when a step is planned - see NextStep.tsx. A card
              that has already been converted is out of the pipeline and needs no
              next contact, so it is not asked for one either. */}
          {!lead.convertedClientId ? (
            <NextStepBadge nextStepAt={lead.nextStepAt} onPlan={onPlanStep} />
          ) : null}
        </div>
        {/* The desk's own labels, read-only here. A board card is 240px wide and
            an editor on it would be a text box nobody could see what they typed
            in; the labels are set on the record's own page. */}
        {lead.tags.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1">
            {lead.tags.map((tag) => (
              <span key={tag} className="crm-chip">
                {tag}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      {busy ? (
        <Loader2 className="mt-2 size-3.5 animate-spin text-muted-foreground" />
      ) : (
        <Select value={lead.status} onValueChange={(v) => changeStatus(v as LeadStatus)}>
          <SelectTrigger className="mt-2 h-6 text-[10px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => (
              <SelectItem key={s.key} value={s.key} className="text-xs">
                {t(`leads.status.${s.key}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Portalled out of this card, so a board column that clips its contents
          cannot clip the question. */}
      {confirmDel ? (
        <ConfirmModal
          title={t("leads.deleteTitle")}
          body={t("leads.deleteConfirm", { name: lead.name })}
          confirmLabel={t("common.delete")}
          cancelLabel={t("common.cancel")}
          onConfirm={doDelete}
          onClose={() => setConfirmDel(false)}
        />
      ) : null}
    </div>
  );
}
